import { useEffect, useState } from "react";

// Color variants for the loader
const variants: Record<string, { primary: string; fade: string }> = {
    bronze: {
        primary: "#b08d57", // bronze-500 (default color)
        fade: "#fff", // white (color when expanded)
    },
    white: {
        primary: "#a8a29e", // stone-400 (default color)
        fade: "#292524", // stone-950 (color when expanded)
    },
    clear: {
        primary: "#a8a29e", // stone-400 (default color)
        fade: "#292524", // stone-950 (color when expanded)
    },
};

interface ButtonLoaderProps {
    variant: keyof typeof variants;
}

// ButtonLoader: Animated loader with 3 lines that grow and shrink in sequence.
// Each line starts as a 4px circle, grows to 12px tall, and changes color based on the variant.
// The animation grows each line one by one, then collapses them one by one from left to right, repeating forever.
const ButtonLoader: React.FC<ButtonLoaderProps> = ({ variant }) => {
    // Get the color values for the selected variant
    const { primary, fade } = variants[variant];

    // State for the height of each line (4px = circle, 12px = expanded)
    const [heights, setHeights] = useState<number[]>([4, 4, 4]);

    // State for the color of each line
    const [colors, setColors] = useState<string[]>([primary, primary, primary]);

    const baseTimeIncrement = 300;

    useEffect(() => {
        let isMounted = true;
        let collapseTimeouts: Array<NodeJS.Timeout | undefined> = [];
        let growTimeouts: Array<NodeJS.Timeout | undefined> = [];

        // Animate the growing phase: each line grows one by one
        const growAll = () => {
            if (!isMounted) {
                return;
            }

            [0, 1, 2].forEach(i => {
                growTimeouts[i] = setTimeout(() => {
                    // Expand the current line and change its color
                    setHeights(prev => prev.map((h, idx) => (idx === i ? 12 : h)));
                    setColors(prev => prev.map((c, idx) => (idx === i ? fade : c)));

                    // After the last line grows, start collapsing after baseTimeIncrement in ms
                    if (i === 2) {
                        setTimeout(() => collapseAll(), baseTimeIncrement);
                    }
                }, i * baseTimeIncrement);
            });
        };

        // Animate the collapsing phase: each line shrinks one by one from left to right
        const collapseAll = () => {
            if (!isMounted) {
                return;
            }

            [0, 1, 2].forEach(i => {
                collapseTimeouts[i] = setTimeout(() => {
                    // Shrink the current line and restore its color
                    setHeights(prev => prev.map((h, idx) => (idx === i ? 4 : h)));
                    setColors(prev => prev.map((c, idx) => (idx === i ? primary : c)));
                }, i * baseTimeIncrement);
            });
            // After all lines are collapsed, restart the grow phase
            collapseTimeouts[3] = setTimeout(() => growAll(), 3 * baseTimeIncrement + baseTimeIncrement); // 3*baseTimeIncrement collapse + baseTimeIncrement pause
        };

        // Start the animation loop
        growAll();

        // Cleanup all timeouts on unmount
        return () => {
            isMounted = false;
            [...growTimeouts, ...collapseTimeouts].forEach(t => t && clearTimeout(t));
        };
    }, [primary, fade]);

    return (
        <div className="flex h-3 items-center">
            {[0, 1, 2].map(i => (
                <span
                    key={i}
                    className={`inline-block ${i !== 2 ? "mr-1.5" : ""}`}
                    style={{
                        // 4px wide
                        width: 4,
                        // Animated height
                        height: heights[i],
                        // Circle when collapsed, pill when expanded
                        borderRadius: heights[i] === 4 ? "50%" : 2,
                        // Animated color
                        background: colors[i],
                        transition:
                            "height 0.3s cubic-bezier(0.1,0.5,0.5,0.9), border-radius 0.3s cubic-bezier(0.1,0.5,0.5,0.9), background 0.3s cubic-bezier(0.8,0.2,0.2,0.8)",
                    }}
                />
            ))}
        </div>
    );
};

export default ButtonLoader;
